import { captureMessage } from '@sentry/react';
import { catchError, map, Observable, of, pipe } from 'rxjs';
import { C_Global_Error_Code } from '../../graphql/generatedModel';
// The bare i18next singleton — the IDENTICAL runtime object `../../i18n/init`
// default-exports (init.ts calls `i18n.use(initReactI18next).init(...)` on
// this same instance, in place, at app boot via main.tsx). Importing the
// singleton directly instead of `../../i18n/init` is deliberate: init.ts
// statically imports `main.i18n.ts` → all 15 feature `*.i18n.ts` files, and
// this module is itself imported by ~80 files across the app — routing that
// edge through here made Vitest's `--changed`/related selection treat any
// single feature-i18n edit as related to ~69% of the test suite. `i18n.t` is
// only called inside functions (never at module-eval time), so all this
// module needs is the singleton reference; whoever boots the app (main.tsx)
// or the test harness (src/test-utils/i18n.ts) is responsible for init, same
// contract as before.
import i18n from 'i18next';
import { snackbarService, ISnackbarOptions } from '../components/snackbar/service/snackbar.service';
import { BROWSER_NETWORK_FAILURE_PATTERNS } from '../helpers/networkError/networkError.helper';

// Message fragments that identify a *transient* server/DB connection drop rather
// than an application bug: a stale pooled connection, an Aurora failover, an
// RDS-Proxy connection recycle, or a plain socket reset. The backend surfaces
// these as a bodyless HTTP 500 whose message is a low-level driver string such as
// "Connection lost: The server closed the connection." (Sentry WAWIASSIST-211 /
// WA-3546). Matched case-insensitively against the (already request-id-stripped)
// error message. Same family the backend BAK-211 db-teardown guard handles — this
// covers the query-rejection path that reaches the UI instead of crashing.
//
// The last pattern covers a browser network-layer truncation: when the connection
// is cut off mid-transfer, the received body is shorter than the declared
// Content-Length header and Firefox throws "Content-Length header of network
// response exceeds response Body." (Sentry WAWIASSIST-22Z / WA-3701). It reaches
// the generic `catchError` branch below with no status/code — same transient class,
// same "just retry" UX, so it is suppressed from Sentry the same way.
//
// The BROWSER_NETWORK_FAILURE_PATTERNS appended below are the same class one
// layer further out: the request never left the machine at all, so there is no
// status and no server-side trace ("Failed to fetch", "Load failed", Amplify's
// "A network error has occurred."). Sentry WAWIASSIST-1T2 (2285 events / 760
// users in 90 days), WAWIASSIST-1GB and WAWIASSIST-246 / WA-3800 are all this —
// an offline or sleeping client, retried three times by the AWS client
// middleware before it ever surfaced here. Same "just retry" UX, same absence of
// anything to fix per occurrence, so they get the same treatment.
const CONNECTION_LOST_PATTERNS: readonly RegExp[] = [
  /connection lost/i,
  /server closed the connection/i,
  /connection terminated/i,
  /PROTOCOL_CONNECTION_LOST/i,
  /ECONNRESET/i,
  /EPIPE/i,
  /ETIMEDOUT/i,
  /socket hang up/i,
  /Content-Length header of network response exceeds response Body/i,
  ...BROWSER_NETWORK_FAILURE_PATTERNS,
];

/* The backend answers this ~2 s before its Lambda deadline so the caller gets a GraphQL error with
a request id instead of a bodyless 500 (`runBeforeLambdaTimeout`). It is a string literal rather
than a `C_Global_Error_Code` member because the generated client schema predates the code - and
that is exactly why it used to fall through to "Something went wrong": nothing could match it.

It means something different from every other error here: the request was NOT rejected, it was
abandoned while still running. For a mutation the write usually lands anyway - WA-3956 is a
CreateWA_Offer that was answered at 27 s, committed its offer at 82 s, and was retried by the user
into a duplicate - so the message must not read as "it failed, try again". */
export const REQUEST_TIMED_OUT_CODE = 'GEC81_REQUEST_TIMED_OUT';

export const isRequestTimedOutError = (code: unknown): boolean => code === REQUEST_TIMED_OUT_CODE;

const isConnectionLostError = (data: IApiError): boolean =>
  typeof data.message === 'string' &&
  CONNECTION_LOST_PATTERNS.some((pattern) => pattern.test(data.message as string));

export const handler = <B>(res: IErrorsHandler<B>) => {
  const { error, success, errorReturnType, quite = false } = res;
  const data = res.data as IApiError;
  let message: string | undefined = undefined;
  let reportToSentry = true;

  if (data.issuer) {
    if (typeof error === 'function') {
      const customErrorResponse = error(data);
      if (typeof customErrorResponse === 'object') {
        message = customErrorResponse.message;
        reportToSentry = customErrorResponse.reportToSentry;
      } else message = customErrorResponse || undefined;
    }

    const isExpectedUserInputError = data.code === C_Global_Error_Code.GEC30_BAD_USER_INPUT;
    // The customer database is mid-upgrade: a release that raises the required DB version goes
    // live before the tenant schemas have caught up, so for the few seconds until the async
    // upgrade lands, anything touching a column that migration added fails. The backend
    // recognises that state and sends GEC50 instead of the raw `Unknown column '...'`
    // (WA-3776, Sentry WAWIASSIST-23Y). It is self-healing and the user is told to reload
    // below, so it is not per-occurrence actionable — same argument as WA-3546.
    const isDbUpdatingError = data.code === C_Global_Error_Code.GEC50_DB_IS_IN_UPDATING_PROCCES;
    // A transient connection drop (see CONNECTION_LOST_PATTERNS) is an
    // infrastructure blip, not an application bug, and not actionable per
    // occurrence — the user just needs to retry.
    const isTransientConnectionError = isConnectionLostError(data);
    // The query matched more rows than the API can deliver in one response. The
    // backend compresses what it can and only sends GEC80 once even that does not
    // fit (WA-3830), so nothing is broken and nothing is fixable per occurrence —
    // the user narrows the date range or the filters and tries again, which is
    // exactly what the message below says. Before GEC80 existed this arrived as a
    // bodyless HTTP 500 and auto-filed a Jira ticket for every occurrence. The
    // backend still logs each one, so the volume is trackable in CloudWatch.
    const isResponseTooLargeError = data.code === C_Global_Error_Code.GEC80_RESPONSE_TOO_LARGE;
    // The company has no WaWiAssist database at all (CashAssist/LabelAssist-only tenants
    // reached through the console company switch, or a login linked to such a company).
    // The backend used to fail on its connection init script with a raw
    // "Table '<guid>_fakturaassist.fkCountries' doesn't exist" (WA-3779, WA-3792, WA-3793,
    // WA-3823, WA-3885 — one Jira ticket per company GUID, because the GUID sits in the
    // message). It now answers GEC53 (WA-3885). It is a provisioning gap for ops, not a
    // client bug, and nothing the user can fix by retrying — so no Sentry, and the
    // snackbar says what is wrong instead of "an error occurred".
    const isCompanyDbNotProvisionedError =
      data.code === C_Global_Error_Code.GEC53_COMPANY_DB_NOT_PROVISIONED;

    // Skip Sentry for a genuinely expired session (flagged by gqlClient): the
    // user is already being redirected to login, so this is a UX lifecycle
    // event, not a bug. This is the path that auto-filed Jira tickets
    // (WA-3514…WA-3538) — every one of those is a `captureMessage` from here,
    // which the `__sentryIgnore` flag on `beforeSend` can never reach.
    // Also skip a transient connection drop: it is a handled infrastructure blip
    // (same family as the backend BAK-211 db-teardown guard) that only produced
    // Jira ticket noise (WA-3546); the user still sees a clear retry message below.
    // Same for GEC50 (see isDbUpdatingError above): auto-filed WA-3776 for a state
    // that had already healed by the time the ticket existed, and for GEC80, which
    // replaced the bodyless 500 that auto-filed WA-3830.
    if (
      reportToSentry &&
      !data.__sessionExpired &&
      !isExpectedUserInputError &&
      !isTransientConnectionError &&
      !isDbUpdatingError &&
      !isResponseTooLargeError &&
      !isCompanyDbNotProvisionedError
    ) {
      captureMessage(data.message || 'unknown', {
        level: 'error',
        extra: { details: data },
      });
    }

    if (message === undefined) {
      if (isRequestTimedOutError(data.code)) {
        message = 'errors.requestTimedOut';
      } else if (isTransientConnectionError) {
        message = 'errors.connectionLost';
      } else if (data.code && C_Global_Error_Code[data.code as C_Global_Error_Code]) {
        message = `errors.${data.code}`;
      } else {
        switch (data.status) {
          case 500:
            message = 'errors.unknown';
            break;
          default:
            message = 'errors.unknown';
            break;
        }
      }
    }

    if (!quite && message) {
      snackbarActions({ errorCode: data.code, errorMessage: message });
    }

    return errorReturnType as B;
  }

  if (typeof success === 'function') {
    if ((message = success(data as B))) {
      snackbarService.pub.show({
        content: i18n.t(message),
        type: 'success',
      });
    }
  }
  return data as B;
};

const errorHandlerPipe = pipe(
  map<any, any>((res) => {
    const { errors } = res;
    if (errors) {
      return {
        issuer: 'graphQl',
        // Need review graphql errors
        status: 500,
        message: errors[0].message,
        code: errors[0].extensions.code,
        invalidArgs: errors[0].extensions.invalidArgs,
        linkedRecords: errors[0].extensions.linkedRecords,
      } as IApiError;
    }
    return res;
  }),
  catchError<any, any>((err) => {
    const { message, response = {} } = err ?? {};
    const { errors, status } = response;
    const model: IApiError = {
      issuer: 'catchError',
      status,
      message,
    };
    // Carry gqlClient's session-expiry marker onto the rebuilt model. The flag
    // lives on the original error; this `catchError` constructs a fresh object,
    // so without copying it here it is lost before `handler` decides whether to
    // report to Sentry — the same way `__sentryIgnore` is lost on this path.
    if ((err as { __sessionExpired?: boolean })?.__sessionExpired) {
      model.__sessionExpired = true;
    }
    if (errors) {
      model.code = errors[0].extensions.code?.toString?.();
      model.message = errors[0].message.replace(/requestid.+?[',"].+?[',"]/im, '');
      model.invalidArgs = errors[0].extensions.invalidArgs;
      model.linkedRecords = errors[0].extensions.linkedRecords;
    }
    return of(model);
  }),
);

export const responseHandler = <T = any>(variables?: IServiceErrorHandler<T>) => {
  return (src: Observable<T>) => {
    return src.pipe(
      errorHandlerPipe,
      map((data) => {
        return handler<T>({
          data,
          error: variables?.customErrorHandler,
          success: variables?.success,
          errorReturnType: variables?.errorReturnType,
          quite: variables?.quite,
        });
      }),
    );
  };
};

const snackbarActions = ({
  errorCode,
  errorMessage,
}: {
  errorCode: string | undefined;
  errorMessage: string;
}): void => {
  const commonSnackbarParams: ISnackbarOptions = {
    preventDuplicate: true,
    content: i18n.t(errorMessage),
    noAutoHide: true,
    type: 'error',
  };

  switch (errorCode) {
    case C_Global_Error_Code.GEC50_DB_IS_IN_UPDATING_PROCCES: {
      snackbarService.pub.show({
        ...commonSnackbarParams,
        type: 'warning',
        showReload: true,
      });
      break;
    }
    case C_Global_Error_Code.GEC53_COMPANY_DB_NOT_PROVISIONED: {
      // A reload cannot help here: the company has to be provisioned (or left) first.
      snackbarService.pub.show({
        ...commonSnackbarParams,
        type: 'warning',
      });
      break;
    }
    default: {
      snackbarService.pub.show(commonSnackbarParams);
      break;
    }
  }
};

interface IServiceErrorHandler<T = unknown> {
  customErrorHandler?: IErrorsHandler<T>['error'];
  success?: IErrorsHandler<T>['success'];
  errorReturnType?: IErrorsHandler<T>['errorReturnType'];
  quite?: IErrorsHandler<T>['quite'];
}

export type LocalizationKey = string;

export interface IErrorsHandler<H> {
  data: any;
  error?: (err: IApiError) =>
    | {
        message?: LocalizationKey;
        reportToSentry: boolean;
      }
    | LocalizationKey
    | void;
  success?: (data: H) => LocalizationKey | undefined;
  errorReturnType?: H;
  quite?: boolean;
}
export interface IApiError {
  issuer?: 'catchError' | 'graphQl';
  status?: number;
  code?: string;
  message?: string;
  linkedRecords?: string;
  invalidArgs?: string[];
  /**
   * Set by gqlClient (via the catchError above) when a 401 was caused by a
   * genuinely expired session that has already triggered redirect-to-login.
   * Suppresses the Sentry `captureMessage` for this error so it does not
   * auto-file a Jira ticket. A fresh-token-still-401 (real backend bug) does
   * NOT carry this flag and is still reported.
   */
  __sessionExpired?: boolean;
}
